import { z } from 'zod'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import timezone from 'dayjs/plugin/timezone.js'
import { MESSAGE_MAX_CHARACTERS } from '@/utils/constants/forms'
import {
  LATIN_ONLY,
  LATIN_AND_NUMBERS_ONLY,
  TICKET_REGEX,
  LATIN_NUMBERS_AND_DASH,
  ALPHA_WITH_SPACES
} from '@/utils/constants/regex'
import { zodCustomErrorMapSettings } from '@/utils/zodUtils'

z.setErrorMap(zodCustomErrorMapSettings)

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Europe/Riga')

// Specific value transformation is necessary so that JAVA api can handle the data
export const SGender = z.object({
  gender: z.string().transform((value) => (value.includes('-') ? '' : value))
})

export const SBookingNumber = z.object({
  bookingNumber: z.string().length(6, { message: 'forms.booking.number.error.length.6' })
})

export const SFirstName = z.object({
  firstName: z
    .string()
    .min(1, { message: 'forms.global.error.required' })
    .regex(LATIN_ONLY, { message: 'forms.global.error.latin' })
    .max(50, { message: 'forms.global.error.max.length.50' })
})

export const SLastName = z.object({
  lastName: z
    .string()
    .min(1, { message: 'forms.global.error.required' })
    .min(2, { message: 'forms.global.error.min.length.2' })
    .regex(ALPHA_WITH_SPACES, { message: 'forms.global.error.latin' })
    .max(50, { message: 'forms.global.error.max.length.50' })
})

export const SMembershipNumber = z.object({
  membershipNumber: z
    .string()
    .regex(new RegExp(/^\d+$/), { message: 'forms.global.error.numbers.only' })
    .length(10, { message: 'forms.global.error.numbers.length.10' })
})

export const SDateOfBirth = z.object({
  dateOfBirth: z
    .string()
    .min(1, { message: 'forms.global.error.required' })
    .refine((value) => dayjs(value).isBefore(new Date()), {
      message: 'forms.global.error.date.past'
    })
})

export const SPhoneCode = z.object({
  phoneCode: z.string().min(1, { message: 'forms.global.error.required' })
})

export const SPhoneNumber = z.object({
  phoneNumber: z.string().min(1, { message: 'forms.global.error.required' })
})

export const SFlightNumber = z.object({
  flightNumber: z
    .string()
    .regex(LATIN_AND_NUMBERS_ONLY, { message: 'forms.global.error.latinAndNumber' })
    .min(1, { message: 'forms.global.error.required' })
    .max(10, { message: 'forms.global.error.max.length.10' })
})

export const SOrigin = z.object({
  origin: z
    .string()
    .regex(LATIN_ONLY, { message: 'forms.global.error.latin' })
    .min(1, { message: 'forms.global.error.required' })
    .max(50, { message: 'forms.global.error.max.length.50' })
})

export const SDestination = z.object({
  destination: z
    .string()
    .regex(LATIN_ONLY, { message: 'forms.global.error.latin' })
    .min(1, { message: 'forms.global.error.required' })
    .max(50, { message: 'forms.global.error.max.length.50' })
})

export const SEmailAddress = z.object({
  emailAddress: z.string().min(1, { message: 'forms.global.error.required' }).email('forms.global.error.emailAddress')
})

export const SMessage = z.object({
  message: z
    .string()
    .min(1, { message: 'forms.global.error.required' })
    .max(MESSAGE_MAX_CHARACTERS, { message: `forms.global.error.max.length.${MESSAGE_MAX_CHARACTERS}` })
})

export const FlightDate = z.string().min(1, { message: 'forms.global.error.required' })

const minDate = dayjs('2010').startOf('year')
const maxDate = dayjs().add(1, 'year').tz()

export const SFlightDateRange = z.object({
  flightDate: FlightDate.refine((value) => dayjs(value).add(1, 'h').tz().isBetween(minDate, maxDate), {
    message: `forms.global.error.date.range.${minDate.year()}.${maxDate.year()}`
  })
})

export const SPreferedFareType = z.object({
  preferedFareType: z.string().min(1, { message: 'forms.global.error.required' })
})

export const SBookingVerificationLastName = z.object({
  lastName: z
    .string()
    .min(1, { message: 'forms.global.error.required' })
    .regex(ALPHA_WITH_SPACES, { message: 'forms.global.error.latin' })
    .max(50, { message: 'forms.global.error.max.length.50' })
})

export const SPnr = z.object({
  pnr: z
    .string()
    .min(1, { message: 'forms.global.error.required' })
    .regex(LATIN_NUMBERS_AND_DASH, { message: 'forms.global.error.latin' })
    .refine(
      (value) => {
        if (TICKET_REGEX.test(value)) return true

        // If it looks like a ticket number (has a dash), don't check for 0/1
        if (value.includes('-')) return true

        if (value.includes('0') || value.includes('1')) return false

        return true
      },
      { message: 'forms.global.error.onesAndZerosNotAllowed' }
    )
})
